import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import {
  ChevronDown,
  Building2,
  Search,
  Check,
  LayoutDashboard,
  Globe,
} from "lucide-react";

/**
 * Organization Switcher for MSP users
 *
 * Allows MSP admins to switch between their aggregate MSP view
 * and individual client organization contexts.
 */
export function OrgSwitcher() {
  const navigate = useNavigate();
  const {
    organization,
    isMsp,
    mspClients,
    activeClientOrg,
    setActiveClient,
    isMspLoading,
  } = useAuthStore();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Don't render if not MSP
  if (!isMsp || !organization) {
    return null;
  }

  // Filter clients based on search
  const filteredClients = mspClients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Current display name
  const currentName = activeClientOrg ? activeClientOrg.name : "All Clients";
  const isViewingClient = !!activeClientOrg;

  const handleSelectClient = (clientId: string | null) => {
    setActiveClient(clientId);
    setIsOpen(false);
    setSearchQuery("");

    // Pages that support client context - stay on current page and refresh
    const contextAwarePages = [
      "/dashboard",
      "/domains",
      "/sources",
      "/alerts",
      "/reports",
    ];

    const currentPath = window.location.pathname;
    const isOnContextAwarePage = contextAwarePages.some(
      (page) => currentPath === page || currentPath.startsWith(`${page}/`)
    );

    if (clientId) {
      // Switching to a client
      if (isOnContextAwarePage) {
        // Stay on current page - data will refresh with new client context
        // Force a re-render by navigating to same path
        navigate(currentPath, { replace: true });
        // Trigger data refresh
        window.dispatchEvent(new CustomEvent("msp-client-changed"));
      } else {
        // Navigate to dashboard for this client context
        navigate("/dashboard");
      }
    } else {
      // Returning to MSP aggregate view
      if (currentPath.startsWith("/msp")) {
        // Stay on MSP pages
        navigate(currentPath, { replace: true });
      } else {
        // Navigate to MSP dashboard
        navigate("/msp");
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors border border-slate-200"
      >
        <Building2 className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700 max-w-[140px] truncate">
          {currentName}
        </span>
        {isViewingClient && (
          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">
            CLIENT
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>

          {/* MSP Aggregate View Option */}
          <div className="p-2 border-b border-slate-100">
            <button
              onClick={() => handleSelectClient(null)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                !activeClientOrg
                  ? "bg-amber-50 text-amber-700"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">All Clients</div>
                <div className="text-xs text-slate-500">MSP Dashboard</div>
              </div>
              {!activeClientOrg && <Check className="w-4 h-4 text-amber-600" />}
            </button>
          </div>

          {/* Client List */}
          <div className="max-h-64 overflow-y-auto p-2">
            {isMspLoading ? (
              <div className="text-center py-4 text-slate-500 text-sm">
                Loading clients...
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">
                {searchQuery ? "No clients match your search" : "No clients yet"}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                      activeClientOrg?.id === client.id
                        ? "bg-amber-50 text-amber-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <Globe className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{client.name}</div>
                      <div className="text-xs text-slate-500">
                        {client.domain_count || 0} domain{(client.domain_count || 0) !== 1 ? "s" : ""}
                      </div>
                    </div>
                    {activeClientOrg?.id === client.id && <Check className="w-4 h-4 text-amber-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-slate-100 bg-slate-50">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate("/msp/clients/new");
              }}
              className="w-full px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-md transition-colors text-center font-medium"
            >
              + Add New Client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
